import React from 'react';
import { cn } from '../../lib/utils';
import { StatusIndicator } from './Icons';
import { Check as CheckIcon, Minus as MinusIcon } from 'lucide-react';

export interface RegistryRowProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  metadata?: React.ReactNode;
  actions?: React.ReactNode;
  avatar?: React.ReactNode;
  /** Supplemental data points shown as columns on large screens */
  cells?: React.ReactNode[];
  className?: string;
  onClick?: () => void;
  /** Selection state */
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  selectable?: boolean;
}

export function SilkCheckbox({ 
  checked, 
  onChange, 
  indeterminate,
  className 
}: { 
  checked?: boolean; 
  onChange?: (checked: boolean) => void;
  indeterminate?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onChange?.(!checked);
      }}
      className={cn(
        "flex h-4 w-4 3xl:h-6 3xl:w-6 shrink-0 items-center justify-center rounded border transition-all duration-150",
        checked || indeterminate 
          ? "bg-primary border-primary text-primary-foreground shadow-sm" 
          : "bg-background border-border/60 hover:border-border",
        className
      )}
    >
      {checked && !indeterminate && <CheckIcon className="h-3 w-3 3xl:h-4 3xl:w-4 stroke-[3px]" />}
      {indeterminate && <MinusIcon className="h-3 w-3 3xl:h-4 3xl:w-4 stroke-[3px]" />}
    </button>
  );
}

export function RegistryRow({
  title,
  subtitle,
  metadata,
  actions,
  avatar,
  cells = [],
  className,
  onClick,
  selected = false,
  onSelect,
  selectable = false,
}: RegistryRowProps) {
  const hasCells = cells.length > 0;
  const isTableMode = hasCells || selectable;
  
  // Base grid: [Selection] [Title/Avatar] [Cells...] [Actions]
  const gridCols = selectable 
    ? "xl:grid-cols-[40px_2fr_repeat(var(--cell-count,1),1fr)_auto]"
    : "xl:grid-cols-[2fr_repeat(var(--cell-count,1),1fr)_auto]";

  return (
    <div
      onClick={onClick}
      className={cn(
        'group flex flex-col gap-2 px-3 py-3 sm:gap-3 sm:px-4 sm:py-4 lg:flex-row lg:items-center lg:justify-between',
        '3xl:px-8 3xl:py-8 4k:px-12 4k:py-10',
        isTableMode && 'xl:grid xl:gap-6 xl:items-center xl:justify-start',
        isTableMode && gridCols,
        selected ? 'bg-primary/5' : '',
        onClick && 'cursor-pointer hover:bg-muted/30 transition-colors',
        className
      )}
      style={{ '--cell-count': cells.length } as React.CSSProperties}
    >
      {selectable && (
        <div className="hidden xl:flex items-center justify-center shrink-0">
          <SilkCheckbox checked={selected} onChange={onSelect} />
        </div>
      )}

      <div className={cn(
        "flex min-w-0 items-start gap-3 sm:gap-4 3xl:gap-8",
        isTableMode && "xl:col-span-1"
      )}>
        {/* On mobile, show checkbox inline with avatar/title */}
        {selectable && (
          <div className="xl:hidden pt-1">
            <SilkCheckbox checked={selected} onChange={onSelect} />
          </div>
        )}
        
        {avatar}
        <div className="min-w-0 flex-1 space-y-0.5 sm:space-y-1 3xl:space-y-2">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 3xl:gap-4">
            <div className="text-[13px] sm:text-sm 3xl:text-2xl 4k:text-3xl font-bold text-foreground font-heading truncate">{title}</div>
            {metadata}
          </div>
          {subtitle && (
            <div className="text-[11px] sm:text-xs 3xl:text-lg text-muted-foreground line-clamp-2 leading-relaxed font-medium">
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Render cells as columns on desktop */}
      {cells.map((cell, idx) => (
        <div key={idx} className="hidden xl:flex items-center text-sm 3xl:text-lg text-muted-foreground font-medium truncate px-2">
          {cell}
        </div>
      ))}

      {actions && (
        <div className={cn(
          "flex items-center gap-1.5 sm:gap-2 shrink-0 3xl:gap-4",
          isTableMode && "xl:justify-end xl:col-span-1"
        )}>
          {actions}
        </div>
      )}
    </div>
  );
}

export interface RegistryListProps {
  children: React.ReactNode;
  className?: string;
  /** Headers to show if the list is in "Table Mode" */
  headers?: string[];
  /** Selection support */
  selectable?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onSelectAll?: (selected: boolean) => void;
}

export function RegistryList({ 
  children, 
  className, 
  headers,
  selectable = false,
  allSelected = false,
  someSelected = false,
  onSelectAll,
}: RegistryListProps) {
  const hasHeaders = headers && headers.length > 0;
  
  const gridCols = selectable 
    ? "xl:grid-cols-[40px_2fr_repeat(var(--cell-count,1),1fr)_auto]"
    : "xl:grid-cols-[2fr_repeat(var(--cell-count,1),1fr)_auto]";

  return (
    <div
      className={cn(
        'rounded-lg border border-border/50 bg-background divide-y divide-border/40 overflow-hidden shadow-sm',
        className
      )}
    >
      {hasHeaders && (
        <div 
          className={cn(
            "hidden xl:grid gap-6 px-4 py-3 bg-muted/30 border-b border-border/40 items-center",
            "3xl:px-8 3xl:py-5 4k:px-12 4k:py-6",
            gridCols
          )}
          style={{ '--cell-count': headers.length - 2 } as React.CSSProperties}
        >
          {selectable && (
            <div className="flex items-center justify-center">
              <SilkCheckbox 
                checked={allSelected} 
                indeterminate={someSelected && !allSelected}
                onChange={onSelectAll} 
              />
            </div>
          )}
          {headers.map((header, idx) => (
            <div 
              key={idx} 
              className={cn(
                "text-[10px] sm:text-[11px] 3xl:text-sm font-black uppercase tracking-widest text-muted-foreground/80",
                idx === headers.length - 1 && "text-right"
              )}
            >
              {header}
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

export function BulkActionBar({
  selectedCount,
  actions,
  onClear,
  className
}: {
  selectedCount: number;
  actions: React.ReactNode;
  onClear: () => void;
  className?: string;
}) {
  if (selectedCount === 0) return null;

  return (
    <div
      className={cn(
        "fixed left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-4 duration-200 w-max max-w-[90vw]",
        className
      )}
      style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <div className="flex items-center gap-4 px-4 py-2 bg-foreground text-background rounded-full shadow-2xl border border-background/10 ring-4 ring-foreground/10">
        <div className="flex items-center gap-3 pr-4 border-r border-background/20 shrink-0">
          <button 
            onClick={onClear}
            className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-background/10 transition-colors"
          >
            <MinusIcon className="h-4 w-4" />
          </button>
          <span className="text-sm font-bold tracking-tight whitespace-nowrap">
            {selectedCount} selected
          </span>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {actions}
        </div>
      </div>
    </div>
  );
}

export interface RegistryAvatarProps {
  name: string;
  className?: string;
}

export function RegistryAvatar({ name, className }: RegistryAvatarProps) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={cn(
        'flex h-8 w-8 sm:h-9 sm:w-9 3xl:h-16 3xl:w-16 4k:h-20 4k:w-20 shrink-0 items-center justify-center rounded-lg 3xl:rounded-2xl border border-primary/20 bg-primary/10 text-[10px] sm:text-[11px] 3xl:text-xl font-black text-primary font-heading shadow-sm',
        className
      )}
    >
      {initials}
    </div>
  );
}

export interface RegistryLinkProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export function RegistryLink({ children, onClick, className }: RegistryLinkProps) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={cn(
        'text-sm font-semibold text-primary hover:underline underline-offset-4 decoration-primary/30 transition-all 3xl:text-xl',
        className
      )}
    >
      {children}
    </button>
  );
}

export interface RegistryStatusProps {
  label: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'muted';
  className?: string;
}

const statusStyles = {
  default: 'border-primary/20 bg-primary/10 text-primary',
  success: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  warning: 'border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  error: 'border-destructive/20 bg-destructive/10 text-destructive',
  muted: 'border-border/40 bg-muted/20 text-muted-foreground',
}

export function RegistryStatus({ label, variant = 'default', className }: RegistryStatusProps) {
  const statusMap: Record<string, 'success' | 'warning' | 'error' | 'pending'> = {
    success: 'success',
    warning: 'warning',
    error: 'error',
    default: 'pending',
    muted: 'pending' // fallback
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border 3xl:text-sm 3xl:px-3 3xl:py-1',
        statusStyles[variant],
        className
      )}
    >
      {variant !== 'muted' && <StatusIndicator status={statusMap[variant] || 'pending'} className="h-1.5 w-1.5 3xl:h-2.5 3xl:w-2.5" />}
      {label}
    </span>
  );
}
